package com.schoolconnect.app.repository.calendar

import com.schoolconnect.app.datasource.network.KtorApiClient
import com.schoolconnect.app.datasource.network.request.CalendarEventBulkCreateRequest
import com.schoolconnect.app.datasource.network.request.CalendarEventUpdateRequest
import com.schoolconnect.app.datasource.network.request.CalendarImportCommitRequest
import com.schoolconnect.app.datasource.network.response.CalendarImportCommitResponse
import com.schoolconnect.app.datasource.network.response.CalendarImportValidationResponse
import com.schoolconnect.app.datasource.network.response.CalendarEventPublicResponse
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.repository.common.RepositoryCache
import com.schoolconnect.app.repository.common.tenantCacheKey
import com.schoolconnect.app.util.scLog
import kotlinx.coroutines.CoroutineScope

data class CalendarEventsPage(val items: List<CalendarEventPublicResponse>, val totalCount: Int)

object CalendarRepository {
    private val pageCache = RepositoryCache<CalendarEventsPage>()

    suspend fun loadEvents(
        pageIndex: Int = 0,
        pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
        status: String? = null,
        eventType: String? = null,
        academicYearId: String? = null,
        fromDate: String? = null,
        toDate: String? = null,
        sortBy: String? = null,
        order: String? = null,
        refreshScope: CoroutineScope
    ): Result<CalendarEventsPage> {
        val safeSize = pageSize.coerceIn(1, 100)
        val safeOffset = pageIndex.coerceAtLeast(0) * safeSize
        val key = tenantCacheKey("calendar-events:$safeOffset:$safeSize:${status.orEmpty()}:${eventType.orEmpty()}:${academicYearId.orEmpty()}:${fromDate.orEmpty()}:${toDate.orEmpty()}:${sortBy.orEmpty()}:${order.orEmpty()}")
        return pageCache.load(refreshScope, "[DATA_RETRIEVE_SC] CalendarRepository", "loadEvents", key) {
            KtorApiClient.getCalendarEvents(safeSize, safeOffset, status, eventType, academicYearId, fromDate, toDate, sortBy, order)
                .map { CalendarEventsPage(it.items, it.count) }
        }
    }

    suspend fun createEvent(request: CalendarEventBulkCreateRequest) = KtorApiClient.createCalendarEvent(request).onSuccess { pageCache.clear() }
    suspend fun getEvent(eventId: String) = KtorApiClient.getCalendarEvent(eventId)
    suspend fun updateEvent(eventId: String, request: CalendarEventUpdateRequest) = KtorApiClient.updateCalendarEvent(eventId, request).onSuccess { pageCache.clear() }
    suspend fun deleteEvent(eventId: String) = KtorApiClient.deleteCalendarEvent(eventId).onSuccess { pageCache.clear() }
    suspend fun validateImport(fileName: String, bytes: ByteArray): Result<CalendarImportValidationResponse> =
        KtorApiClient.validateCalendarImport(fileName, bytes)
    suspend fun commitImport(sessionId: String, publish: Boolean): Result<CalendarImportCommitResponse> =
        KtorApiClient.commitCalendarImport(CalendarImportCommitRequest(sessionId.trim(), publish)).onSuccess {
            pageCache.clear()
        }
    suspend fun downloadImportTemplate(): Result<ByteArray> {
        scLog("[DATA_RETRIEVE_SC] CalendarRepository downloadImportTemplate start")
        return KtorApiClient.downloadCalendarImportTemplate()
            .onSuccess { scLog("[DATA_RETRIEVE_SC] CalendarRepository downloadImportTemplate success bytes=${it.size}") }
            .onFailure { scLog("[DATA_RETRIEVE_SC] CalendarRepository downloadImportTemplate failure", it) }
    }

    suspend fun downloadCurrentData(): Result<ByteArray> {
        scLog("[DATA_RETRIEVE_SC] CalendarRepository downloadCurrentData start")
        return KtorApiClient.downloadCalendarCurrentData()
            .onSuccess { scLog("[DATA_RETRIEVE_SC] CalendarRepository downloadCurrentData success bytes=${it.size}") }
            .onFailure { scLog("[DATA_RETRIEVE_SC] CalendarRepository downloadCurrentData failure", it) }
    }
}
